"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import { triggerNavProgress } from "@/components/NavigationProgress";

/**
 * `useRouter()` plus navigation feedback.
 *
 * The app-wide overlay (components/NavigationProgress.tsx) catches clicks on
 * real links, but a programmatic `router.push` is invisible to it, so those
 * clicks sat there doing nothing visible until the next page rendered. Rather
 * than asking every call site to remember `triggerNavProgress()` (21 of 97
 * navigations did), this wrapper fires it for them.
 *
 * Use this instead of `useRouter` in client components. `refresh` and `back`
 * pass through untouched: refresh does not change the URL, so nothing would
 * clear the overlay.
 */
export function useAppRouter() {
  const router = useRouter();

  return useMemo(() => {
    const sameUrl = (href: string) => {
      if (typeof window === "undefined") return false;
      try {
        const url = new URL(href, window.location.href);
        return (
          url.pathname === window.location.pathname &&
          url.search === window.location.search
        );
      } catch {
        return false;
      }
    };

    // A navigation to where we already are changes no URL, so the overlay
    // would hang until its stuck timeout. Skip the signal, keep the push.
    const withProgress =
      <T extends (href: string, ...rest: never[]) => void>(fn: T) =>
      (href: string, ...rest: never[]) => {
        if (!sameUrl(href)) triggerNavProgress();
        fn(href, ...rest);
      };

    return {
      ...router,
      push: withProgress(router.push.bind(router)),
      replace: withProgress(router.replace.bind(router)),
    };
  }, [router]);
}
